import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface BlockOption {
  block_id: number;
  label: string;
}

interface Props {
  blockId: number;
  open: boolean;
  onClose: () => void;
  onCreated: (button: any) => void;
}

const API_URL = import.meta.env.VITE_API_URL;

export function ButtonCreateModal({
  blockId,
  open,
  onClose,
  onCreated,
}: Props) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<
    'none' | 'url' | 'web_app' | 'callback' | 'programmed'
  >('none');
  const [keyboardType, setKeyboardType] = useState<'inline' | 'reply'>(
    'inline',
  );
  const [url, setUrl] = useState('');
  const [callbackBlock, setCallbackBlock] = useState<number | ''>('');
  const [blocks, setBlocks] = useState<BlockOption[]>([]);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [orderX, setOrderX] = useState<number | ''>(1);
  const [orderY, setOrderY] = useState<number | ''>(1);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    async function fetchBlocks() {
      const res = await axios.get<{ data: BlockOption[] }>(`${API_URL}/blocks`);
      setBlocks(res.data.data);
    }
    fetchBlocks();
  }, []);

  const validate = ({ label }: { label: string }) => {
    const newErrors: string[] = [];

    if (!label?.trim()) newErrors.push('Назва кнопки не може бути порожньою');

    if ((type === 'url' || type === 'web_app') && !url.trim())
      newErrors.push('URL не може бути порожнім');

    if (type === 'callback' && !callbackBlock)
      newErrors.push('Оберіть блок для переходу');

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleCreate = async () => {
    if (!validate({ label })) return;

    const payload = {
      blockId,
      label,
      type,
      keyboardType,
      url: type === 'url' || type === 'web_app' ? url : null,
      callback: null,
      nextBlockId: type === 'callback' ? callbackBlock : null,
      isFullWidth,
      orderX: !isFullWidth ? orderX : null,
      orderY: !isFullWidth ? orderY : null,
    };

    try {
      const res = await axios.post(`${API_URL}/content/button`, payload);

      onCreated(res.data);
      onClose();

      setLabel('');
      setType('none');
      setKeyboardType('inline');
      setUrl('');
      setCallbackBlock('');
      setIsFullWidth(false);
      setOrderX(1);
      setOrderY(1);
      setErrors([]);
    } catch (err: any) {
      const error = err?.response?.data?.error;

      if (Array.isArray(error)) {
        setErrors(error);
      } else if (error) {
        setErrors([error]);
      } else {
        setErrors(['Сталася помилка']);
      }
    }
  };

  const TYPE_OPTIONS = [
    { value: 'none', label: 'Без дії', description: 'Кнопка без функціоналу' },
    { value: 'url', label: 'Посилання', description: 'Відкрити посилання' },
    { value: 'web_app', label: 'Web App', description: 'Запустити Web App' },
    { value: 'callback', label: 'Callback', description: 'Перехід на блок' },
    { value: 'programmed', label: 'Програмна', description: 'Системна кнопка' },
  ];

  const renderTypeBlock = (opt: any) => {
    const disabled = type === 'programmed' || opt.value === 'programmed';

    return (
      <Box
        key={opt.value}
        onClick={() => !disabled && setType(opt.value)}
        sx={{
          p: 2,
          borderRadius: 2,
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: type === opt.value ? '2px solid #4f46e5' : '1px solid #ccc',
          opacity: disabled ? 0.5 : 1,
          width: 150,
        }}
      >
        <Typography fontWeight="bold">{opt.label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {opt.description}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Створити кнопку
        <IconButton edge="end" color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          {errors.length > 0 && (
            <Box
              sx={{
                background: '#fee2e2',
                border: '1px solid #ef4444',
                color: '#7f1d1d',
                borderRadius: 2,
                p: 2,
              }}
            >
              {errors.map((err, i) => (
                <Typography key={i} variant="body2">
                  - {err}
                </Typography>
              ))}
            </Box>
          )}

          <TextField
            label="Напис"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle1" mb={1}>
              Тип кнопки
            </Typography>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
              {TYPE_OPTIONS.map(renderTypeBlock)}
            </Stack>
          </Box>

          {(type === 'url' || type === 'web_app') && (
            <TextField
              label="URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
            />
          )}

          {type === 'callback' && (
            <FormControl fullWidth>
              <InputLabel>Оберіть блок</InputLabel>
              <Select
                value={callbackBlock}
                label="Оберіть блок"
                onChange={(e) => setCallbackBlock(e.target.value as number)}
              >
                {blocks.map((b) => (
                  <MenuItem key={b.block_id} value={b.block_id}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={isFullWidth}
                onChange={(e) => setIsFullWidth(e.target.checked)}
              />
            }
            label="Повна ширина"
          />

          {!isFullWidth && (
            <TextField
              type="number"
              label="Номер рядку"
              value={orderY}
              onChange={(e) => setOrderY(Number(e.target.value))}
              fullWidth
            />
          )}

          {!isFullWidth && (
            <TextField
              type="number"
              label="Порядок у ряду"
              value={orderX}
              onChange={(e) => setOrderX(Number(e.target.value))}
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button variant="contained" onClick={handleCreate}>
          Створити
        </Button>
      </DialogActions>
    </Dialog>
  );
}
